/**
 * screens/dashboard/dashboard.tsx
 * SK Hire home / Dashboard — rebuilt to the design reference:
 *   greeting header + actions · 4 KPI tiles (Active jobs / Candidates ingested /
 *   AI credits) · Recent match runs · Consent expiring ·
 *   Ingest pipeline.
 *
 * Data comes from the DashboardData contract (schemas/hire/home). Currently
 * served by the mock loader; swap for GET /dashboard (v2) when the BE lands.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { View, Text, ScrollView, Pressable, StyleSheet, Platform } from 'react-native';
import { useFocusEffect, useIsFocused } from '@react-navigation/native';

import { useTheme } from '../../context/themeContext';
import { useSession } from '../../context/sessionContext';
import { useBreakpoints, ContentMaxWidth } from '../../theme/responsive';
import { navigateApp } from '../../navigation/navigationRef';
import { web } from '../../utils/web';
import { useHover } from '../../hooks/useHover';
import { useAiUsage, useWorkspaceStats } from '../../hooks/useAiUsage';
import { Icon, StatusChip, ProgressMeter } from '../../components/ui';
import type { Tone } from '../../theme/tone';

/* ── premium helpers: entrance animation + count-up ───────────────────── */

/** CSS entrance (staggered fade + rise) — see @keyframes sk-rise in index.html. */
function rise(delay = 0): object {
  return web({ animation: `sk-rise 480ms cubic-bezier(.2,.8,.2,1) both`, animationDelay: `${delay}ms` });
}

/** Returns true one frame after mount — lets CSS transitions animate from an
 *  initial value to the final one (e.g. a bar growing from 0 width). */
function useMounted(): boolean {
  const [m, setM] = useState(Platform.OS !== 'web');
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const raf = requestAnimationFrame(() => setM(true));
    return () => cancelAnimationFrame(raf);
  }, []);
  return m;
}

/** Animate a number from 0 → target on mount (web only). */
function useCountUp(target: number, duration = 520): number {
  const [n, setN] = useState(Platform.OS === 'web' ? 0 : target);
  useEffect(() => {
    if (Platform.OS !== 'web') { setN(target); return; }
    let raf = 0;
    const start = performance.now();
    const tick = (t: number) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return n;
}
import { loadDashboard } from './dashboard.mock';
import AddCandidateV2Modal from '../../components/hire/addCandidateV2Modal';
// Bulk import, reachable straight from the dashboard for both record types.
import BulkUploadModal, { type BulkKind } from '../../components/hire/bulkUploadModal';
import type { DashboardData, DashboardMatchRun, RerankStatus, MatchTrigger } from '../../schemas/hire/home';

/* ── small presentational helpers ─────────────────────────────────────── */

function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

function dateLine(): string {
  return new Date().toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

function relTime(iso: string): string {
  const then = new Date(iso).getTime();
  const mins = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return days === 1 ? 'yesterday' : `${days}d ago`;
}

const TRIGGER_LABEL: Record<MatchTrigger, string> = {
  activation: 'on activation',
  manual: 'manual run',
  nightly: 'nightly sweep',
};

function rerankVM(status: RerankStatus): { tone: Tone; label: string } {
  switch (status) {
    case 'ok': return { tone: 'success', label: 'rerank ok' };
    case 'fallback': return { tone: 'warn', label: 'fallback' };
    case 'skipped_no_credits': return { tone: 'danger', label: 'no credits' };
    case 'skipped_incremental': return { tone: 'neutral', label: 'incremental' };
    // The status comes from the SERVER, so this switch can go stale the moment a
    // new value ships — and returning undefined here white-screened the whole
    // dashboard via `vm.tone`. Degrade to the raw value instead of crashing.
    default: return { tone: 'neutral', label: String(status) };
  }
}

/* ── header action buttons (design: 34px, primary accent / outline) ───── */

function ActionButton({ label, variant, icon, onPress, fullWidth = false }: { label: string; variant: 'primary' | 'secondary'; icon?: string; onPress?: () => void; fullWidth?: boolean }) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, radii } = theme;
  const { hovered, hoverProps } = useHover();
  const primary = variant === 'primary';
  return (
    <Pressable
      {...hoverProps}
      onPress={onPress}
      style={[
        {
          flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 7, height: 34, paddingHorizontal: 14,
          borderRadius: radii.sm, borderWidth: 1,
          backgroundColor: primary ? colors.accent : colors.card,
          borderColor: primary ? colors.accent : colors.line,
        },
        fullWidth ? ({ alignSelf: 'stretch', width: '100%' } as object) : null,
        web({ cursor: 'pointer', transition: 'opacity 120ms ease, background 120ms ease' }),
        hovered && web(primary ? { opacity: 0.92 } : { backgroundColor: colors.paper2 }),
      ]}
    >
      {icon && <Icon name={icon} size={14} color={primary ? colors.onAccent : colors.ink2} />}
      <Text
        numberOfLines={1}
        style={[
          { fontSize: fontSize.md, fontWeight: fontWeight.medium, color: primary ? colors.onAccent : colors.ink },
          !icon && { flex: 1, textAlign: 'center' },
        ]}
      >{label}</Text>
    </Pressable>
  );
}

/* ── skeleton shimmer (smooth load — mirrors the real layout) ─────────── */

function Skeleton({ w, h, r = 6, style }: { w?: number | string; h: number; r?: number; style?: object }) {
  const { theme } = useTheme();
  const { colors } = theme;
  return (
    <View
      style={[
        { width: (w as number) ?? '100%', height: h, borderRadius: r, backgroundColor: colors.paper2 },
        web({
          backgroundImage: `linear-gradient(90deg, ${colors.paper2} 0%, ${colors.paper3} 50%, ${colors.paper2} 100%)`,
          backgroundSize: '300% 100%',
          animation: 'sk-shimmer 1.4s ease-in-out infinite',
        }),
        style,
      ]}
    />
  );
}

function SkeletonCard({ delay = 0, children }: { delay?: number; children: React.ReactNode }) {
  const { theme } = useTheme();
  const { colors, radii } = theme;
  return (
    <View
      style={[
        { flex: 1, minWidth: 180, padding: 16, borderRadius: radii.lg, backgroundColor: colors.card, borderWidth: 1, borderColor: colors.line },
        rise(delay),
      ]}
    >
      {children}
    </View>
  );
}

function DashboardSkeleton({ isMobile }: { isMobile: boolean }) {
  const { theme } = useTheme();
  const { colors, radii } = theme;
  return (
    <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: isMobile ? 18 : 32, paddingBottom: 60 }}>
      <View style={{ width: '100%', maxWidth: ContentMaxWidth.default, alignSelf: 'center', gap: 18 }}>
        {/* Header */}
        <View style={[styles.header, rise(0), isMobile && { flexDirection: 'column', alignItems: 'flex-start', gap: 12 }]}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 15 }}>
            <Skeleton w={50} h={50} r={15} />
            <View style={{ gap: 8 }}>
              <Skeleton w={170} h={24} r={999} />
              <Skeleton w={230} h={26} r={8} />
            </View>
          </View>
          <View style={{ flexDirection: 'row', gap: 10 }}>
            <Skeleton w={130} h={34} r={radii.sm} />
            <Skeleton w={92} h={34} r={radii.sm} />
          </View>
        </View>

        {/* KPI grid */}
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 14 }}>
          {[40, 100, 160, 220].map((d, i) => (
            <SkeletonCard key={i} delay={d}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 7, marginBottom: 12 }}>
                <Skeleton w={7} h={7} r={999} />
                <Skeleton w={'55%'} h={12} />
              </View>
              <Skeleton w={90} h={30} r={8} />
              <Skeleton w={'70%'} h={10} style={{ marginTop: 10 }} />
            </SkeletonCard>
          ))}
        </View>

        {/* Two-column */}
        <View style={[{ flexDirection: 'row', gap: 16 }, isMobile && { flexDirection: 'column' }]}>
          <View style={{ flex: isMobile ? undefined : 1.35 }}>
            <View style={[{ backgroundColor: colors.card, borderColor: colors.line, borderWidth: 1, borderRadius: radii.lg, padding: 16, gap: 16 }, rise(280)]}>
              <Skeleton w={'40%'} h={13} />
              {[0, 1, 2, 3].map((i) => (
                <View key={i} style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                  <View style={{ flex: 1, gap: 6 }}><Skeleton w={'60%'} h={12} /><Skeleton w={'85%'} h={10} /></View>
                  <Skeleton w={70} h={22} r={999} />
                </View>
              ))}
            </View>
          </View>
          <View style={{ flex: isMobile ? undefined : 1, gap: 16 }}>
            {[340, 400].map((d, k) => (
              <View key={k} style={[{ backgroundColor: colors.card, borderColor: colors.line, borderWidth: 1, borderRadius: radii.lg, padding: 16, gap: 14 }, rise(d)]}>
                <Skeleton w={'45%'} h={13} />
                {[0, 1, 2].map((i) => (
                  <View key={i} style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Skeleton w={'50%'} h={11} /><Skeleton w={28} h={11} />
                  </View>
                ))}
              </View>
            ))}
          </View>
        </View>
      </View>
    </ScrollView>
  );
}

/* ── card ─────────────────────────────────────────────────────────────── */

/** Soft premium depth for cards — subtle in light, quiet in dark. */
const PANEL_SHADOW = web({ boxShadow: '0 1px 2px rgba(16,24,40,0.05), 0 16px 34px -22px rgba(16,24,40,0.20)' });

function Panel({ title, action, children, delay = 0, icon }: { title: string; action?: React.ReactNode; children: React.ReactNode; delay?: number; icon?: string }) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, radii } = theme;
  return (
    <View style={[{ backgroundColor: colors.card, borderColor: colors.line, borderWidth: 1, borderRadius: radii.lg }, PANEL_SHADOW, rise(delay)]}>
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 16, paddingVertical: 14, borderBottomWidth: 1, borderBottomColor: colors.line }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 9, minWidth: 0 }}>
          {icon && (
            <View style={{ width: 24, height: 24, borderRadius: 7, backgroundColor: colors.paper2, borderWidth: 1, borderColor: colors.line, alignItems: 'center', justifyContent: 'center' }}>
              <Icon name={icon} size={13} color={colors.ink3} />
            </View>
          )}
          <Text style={{ fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: colors.ink }}>{title}</Text>
        </View>
        {action}
      </View>
      <View>{children}</View>
    </View>
  );
}

/* ── screen ───────────────────────────────────────────────────────────── */

export default function DashboardScreen() {
  const { theme, isDark } = useTheme();
  const { colors, fontSize, fontWeight, font } = theme;
  const { user } = useSession();
  const { isMobile } = useBreakpoints();

  const [data, setData] = useState<DashboardData | null>(null);
  const [ingestOpen, setIngestOpen] = useState(false);
  // null = closed; otherwise the record type being imported.
  const [bulkKind, setBulkKind] = useState<BulkKind | null>(null);

  // Track whether the screen is still mounted so late async refetches don't
  // set state on an unmounted screen.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Loads on mount AND on every revisit. React Navigation keeps this screen
  // mounted when you navigate away, so a mount-only fetch would show stale
  // tiles on return — which is why the rev effect below had to run unguarded,
  // and that is what made /dashboard fire while the user was on another screen.
  const load = useCallback(() => {
    loadDashboard().then((d) => { if (mountedRef.current) setData(d); });
  }, []);
  useFocusEffect(load);

  // Live updates: the workspace SSE rev bumps on every mutation (add/close/
  // activate a job, add/edit a candidate, run a match, consent change). When it
  // changes, refetch the whole dashboard — debounced — so EVERY card (jobs by
  // status, candidates, sources, recent runs, ingest, AI tokens) updates without
  // a manual refresh.
  // The debounce window is deliberately wide. ONE user action bumps `rev`
  // several times server-side — creating a candidate emits for the record, the
  // consent entry, the derived profile and the AI-token spend — and at 300ms
  // each bump landed as its own full /dashboard fetch (4 per add). A second-long
  // trailing window collapses that burst into a single refetch.
  const REFETCH_DEBOUNCE_MS = 1_200;
  const isFocused = useIsFocused();
  const { rev } = useWorkspaceStats();
  const lastRev = useRef(rev);
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  const loadRef = useRef(load);
  loadRef.current = load;
  useEffect(() => {
    // THE guard this effect was missing. The Dashboard is the root screen, so
    // React Navigation keeps it mounted for the whole session — without this it
    // refetched on every rev bump while the user was on Job detail, Ops or
    // Candidates, repainting tiles nobody was looking at. Running a match or
    // activating a job emits several bumps over several seconds, so that showed
    // up as a stream of /dashboard calls from a screen the user had left.
    //
    // While unfocused we absorb the bumps and let useFocusEffect(load) above own
    // the single catch-up fetch when the user actually returns.
    if (!isFocused) { lastRev.current = rev; return; }
    if (rev === lastRev.current) return;
    lastRev.current = rev;
    // Nothing to repaint while the browser tab itself is in the background.
    if (Platform.OS === 'web' && typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => loadRef.current(), REFETCH_DEBOUNCE_MS);
    return () => { if (debounce.current) clearTimeout(debounce.current); };
  }, [rev, isFocused]);

  if (!data) {
    return <DashboardSkeleton isMobile={isMobile} />;
  }

  const firstName = user?.first_name || 'there';
  const manualCount = data.sourceSplit.find(s => s.source === 'manual')?.count ?? 0;
  const portalCount = data.sourceSplit.filter(s => s.source !== 'manual').reduce((n, s) => n + s.count, 0);

  // ── hover-detail payloads for the KPI tiles (real breakdowns) ──────────
  const segPalette = [colors.accent, colors.teal, colors.purple, colors.info, colors.warn, colors.neutral];
  const titleCase = (s: string) => s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, ' ');
  const totalJobs = data.activeJobs + data.draftJobs;

  const jobsDetail: KpiDetail = {
    icon: 'layers', title: 'Jobs', subtitle: 'Pipeline status',
    total: data.activeJobs, unit: 'active',
    segments: [
      { label: 'Active', value: data.activeJobs, color: colors.accent },
      { label: 'Draft', value: data.draftJobs, color: colors.neutral },
    ],
    stats: [
      { label: 'Total jobs', value: totalJobs.toLocaleString() },
      { label: 'Recent match runs', value: data.recentRuns.length.toLocaleString() },
    ],
    emptyText: 'No active jobs yet — activate a draft to start matching.',
    footer: 'Active + draft across the workspace · tap to open Jobs.',
  };

  const candidatesDetail: KpiDetail = {
    icon: 'users', title: 'Candidates', subtitle: 'Ingest sources',
    total: data.candidatesIngested, unit: 'ingested',
    segments: data.sourceSplit.map((s, i) => ({ label: titleCase(s.source), value: s.count, color: segPalette[i % segPalette.length] })),
    stats: [
      { label: 'Via portals', value: portalCount.toLocaleString() },
      { label: 'Distinct sources', value: (data.sourceSplit.length || 0).toLocaleString() },
    ],
    emptyText: 'No candidates ingested yet.',
    footer: 'Ingested across every connected source · tap to open Candidates.',
  };


  return (
    <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: isMobile ? 18 : 32, paddingBottom: 60 }}>
      <View style={{ width: '100%', maxWidth: ContentMaxWidth.default, alignSelf: 'center', gap: 18 }}>

        {/* Header — elite, theme-aware: glossy app-icon glyph + gradient-clipped
            greeting. Every colour derives from theme tokens so light & dark both
            read crisply (solid fills flip light↔dark per the design system). */}
        <View style={[styles.header, rise(0), isMobile && { flexDirection: 'column', alignItems: 'flex-start', gap: 16 }]}>
          <View style={[{ flexDirection: 'row', alignItems: 'center', gap: 15, minWidth: 0 }, isMobile && { alignSelf: 'stretch' }]}>
            {/* premium brand glyph: gradient fill + glossy top highlight + depth */}
            <View
              style={[
                { width: 50, height: 50, borderRadius: 15, backgroundColor: colors.accent, alignItems: 'center', justifyContent: 'center', overflow: 'hidden' },
                web({
                  backgroundImage: `linear-gradient(150deg, ${colors.accent}, ${colors.accentInk})`,
                  boxShadow: isDark
                    ? '0 12px 30px -12px rgba(0,0,0,0.85), inset 0 1px 0 rgba(255,255,255,0.45)'
                    : '0 14px 30px -12px rgba(16,24,40,0.55), inset 0 1px 0 rgba(255,255,255,0.20)',
                }),
              ]}
            >
              {/* glossy inner highlight (top half) */}
              <View style={web({ position: 'absolute', top: 0, left: 0, right: 0, height: '55%', backgroundImage: `linear-gradient(180deg, ${isDark ? 'rgba(255,255,255,0.28)' : 'rgba(255,255,255,0.16)'}, transparent)` }) as object} />
              <Icon name="grid" size={23} color={colors.onAccent} />
            </View>
            <View style={{ flex: 1, minWidth: 0 }}>
              {/* date chip — a self-contained pill so it stays crisp & legible in
                  both themes (paper surface + hairline + ink2 text). */}
              <View style={{ flexDirection: 'row', alignItems: 'center', alignSelf: 'flex-start', gap: 6, paddingLeft: 8, paddingRight: 10, height: 24, borderRadius: 999, backgroundColor: colors.paper2, borderWidth: 1, borderColor: colors.line, marginBottom: 6 }}>
                <Icon name="calendar" size={12} color={colors.ink3} />
                <Text style={{ fontSize: 11, color: colors.ink2, letterSpacing: 0.6, fontWeight: fontWeight.medium, fontFamily: font.mono, textTransform: 'uppercase' }}>{dateLine()}</Text>
              </View>
              {/* greeting — gradient-clipped ink→ink2 for depth (web), solid fallback.
                  Wraps (never clips) on narrow viewports. */}
              <Text
                numberOfLines={2}
                style={[
                  { fontSize: isMobile ? 24 : 29, fontWeight: fontWeight.bold, color: colors.ink, letterSpacing: -0.7, lineHeight: isMobile ? 29 : 34 },
                  web({ backgroundImage: `linear-gradient(180deg, ${colors.ink}, ${colors.ink2})`, WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent' }),
                ]}
              >
                {greeting()}, {firstName}
              </Text>
            </View>
          </View>
          <View style={[{ flexDirection: 'row', gap: 10 }, isMobile && { alignSelf: 'stretch' }]}>
            {/* The dashboard's job is to start work, and bulk import is how work
                starts now — a single resume and a jump to the Jobs list were the
                two weakest actions on the screen. */}
            <View style={isMobile ? { flex: 1 } : undefined}>
              <ActionButton label="Bulk import jobs" variant="secondary" icon="upload" fullWidth={isMobile} onPress={() => setBulkKind('job')} />
            </View>
            <View style={isMobile ? { flex: 1 } : undefined}>
              <ActionButton label="Bulk import candidates" variant="primary" icon="upload" fullWidth={isMobile} onPress={() => setBulkKind('candidate')} />
            </View>
          </View>
        </View>

        {/* KPI grid — lifted above the panels below so the AI-tokens hover card
            (which drops down out of this row) paints over them, not behind. */}
        <View style={[{ flexDirection: 'row', flexWrap: 'wrap', gap: isMobile ? 12 : 14 }, web({ position: 'relative', zIndex: 20 })]}>
          <KpiTile label="Active jobs" value={data.activeJobs} sub={`${data.draftJobs} drafts`} tone="accent" delay={30} onPress={() => navigateApp('jobs')} detail={jobsDetail} />
          <KpiTile label="Candidates ingested" value={data.candidatesIngested} sub={`${portalCount} via portals · ${manualCount} manual`} tone="info" delay={65} onPress={() => navigateApp('candidates')} detail={candidatesDetail} />
          <AiUsageTile initial={data.aiUsage} delay={100} />
        </View>

        {/* Two-column: recent runs | (consent expiring + ingest) */}
        <View style={[{ flexDirection: 'row', gap: 16 }, isMobile && { flexDirection: 'column' }]}>
          <View style={{ flex: isMobile ? undefined : 1.35 }}>
            <Panel title="Recent match runs" icon="history" delay={175} action={<Pressable onPress={() => navigateApp('jobs')}><Text style={{ fontSize: fontSize.sm, color: colors.accent, fontWeight: fontWeight.medium }}>All jobs</Text></Pressable>}>
              {data.recentRuns.length === 0 ? (
                <EmptyState icon="history" title="No match runs yet" hint="Run a match from any active job to see it here." />
              ) : (
                data.recentRuns.map((run, i) => <RunRow key={run.id} run={run} index={i} last={i === data.recentRuns.length - 1} />)
              )}
            </Panel>
          </View>

          <View style={{ flex: isMobile ? undefined : 1, gap: 16 }}>
            {/* Jobs by status — the requisition pipeline (active / draft / on hold / closed) */}
            <Panel
              title="Jobs by status"
              icon="layers"
              delay={210}
              action={<Pressable onPress={() => navigateApp('jobs')}><Text style={{ fontSize: fontSize.sm, color: colors.accent, fontWeight: fontWeight.medium }}>All jobs</Text></Pressable>}
            >
              <View style={{ padding: 6 }}>
                {(() => {
                  const rows: { key: string; label: string; tone: Tone }[] = [
                    { key: 'active', label: 'Active', tone: 'success' },
                    { key: 'draft', label: 'Draft', tone: 'neutral' },
                    { key: 'on_hold', label: 'On hold', tone: 'info' },
                    { key: 'closed', label: 'Closed', tone: 'danger' },
                  ];
                  const countOf = (s: string) => data.jobStatusSplit.find((x) => x.status === s)?.count ?? 0;
                  const totalJobsAll = rows.reduce((n, r) => n + countOf(r.key), 0);
                  if (totalJobsAll === 0) {
                    return <EmptyState icon="layers" title="No jobs yet" hint="Create a job to start building your pipeline." />;
                  }
                  const max = Math.max(1, ...rows.map((r) => countOf(r.key)));
                  return rows.map((r, i) => (
                    <IngestRow key={r.key} label={r.label} value={countOf(r.key)} max={max} tone={r.tone} delay={280 + i * 50} last={i === rows.length - 1} />
                  ));
                })()}
              </View>
            </Panel>

            {/* Ingest pipeline */}
            <Panel title="Ingest pipeline" icon="database" delay={250}>
              <View style={{ padding: 6 }}>
                {(() => {
                  const ing = data.ingest;
                  const max = Math.max(1, ing.queued, ing.extracted, ing.embedded, ing.failed);
                  return (
                    <>
                      <IngestRow label="Queued" value={ing.queued} max={max} tone="neutral" delay={280} />
                      <IngestRow label="Extracted" value={ing.extracted} max={max} tone="info" delay={330} />
                      <IngestRow label="Embedded" value={ing.embedded} max={max} tone="success" delay={380} />
                      <IngestRow label="Failed" value={ing.failed} max={max} tone="danger" delay={430} last />
                    </>
                  );
                })()}
              </View>
            </Panel>
          </View>
        </View>
      </View>

      <AddCandidateV2Modal open={ingestOpen} onClose={() => setIngestOpen(false)} onDone={(c) => { setIngestOpen(false); navigateApp('candidateDetail', { id: c.id }); }} />

      {/* One modal, both kinds — it takes the record type as a prop. Reloading on
          completion so the KPI tiles reflect what was just imported. */}
      <BulkUploadModal
        open={bulkKind !== null}
        kind={bulkKind ?? 'job'}
        onClose={() => setBulkKind(null)}
        // No refetch here on purpose. The import's own mutations bump `rev`, and
        // the debounced effect above reloads the tiles once — adding a call here
        // just meant every import cost two full /dashboard fetches.
      />
    </ScrollView>
  );
}

/* ── premium KPI tile (hover-lift · count-up · staggered entrance) ─────── */

interface KpiDetailSeg { label: string; value: number; color: string }
interface KpiDetail {
  icon: string;
  title: string;
  subtitle: string;
  badge?: string;
  total: number;
  unit: string;
  segments?: KpiDetailSeg[];
  stats?: { label: string; value: string }[];
  emptyText?: string;
  footer: string;
}

function KpiTile({ label, value, sub, tone = 'accent', delay = 0, onPress, detail, detailAlign = 'left' }: { label: string; value: number; sub?: string; tone?: Tone; delay?: number; onPress?: () => void; detail?: KpiDetail; detailAlign?: 'left' | 'right' }) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, radii, spacing } = theme;
  const { hovered, hoverProps } = useHover();
  const { isMobile } = useBreakpoints();
  const n = useCountUp(value);
  const dotColor = tone === 'warn' ? colors.warn : tone === 'danger' ? colors.danger : tone === 'success' ? colors.success : tone === 'info' ? colors.info : colors.accent;
  const valueColor = tone === 'warn' && value > 0 ? colors.warn : colors.ink;

  // Hover flyouts never fire on touch, so on mobile we surface the breakdown
  // inline as a compact split bar instead — and drop the (dead) chevron cue.
  const segs = (detail?.segments ?? []).filter((s) => s.value > 0);
  const segTotal = segs.reduce((a, s) => a + s.value, 0) || 1;
  const showFlyout = !!detail && hovered && !isMobile && Platform.OS === 'web';

  return (
    <View
      {...hoverProps}
      style={[
        isMobile ? { flexBasis: '47%', flexGrow: 1, minWidth: 0, position: 'relative' } : { flex: 1, minWidth: 180, position: 'relative' },
        showFlyout ? web({ zIndex: 50 }) : null,
      ]}
    >
      <Pressable
        onPress={onPress}
        style={[
          { padding: spacing[4], borderRadius: radii.lg, backgroundColor: colors.card, borderWidth: 1, borderColor: hovered ? colors.lineStrong : colors.line },
          isMobile ? { flex: 1 } : null,
          rise(delay),
          web({ cursor: onPress ? 'pointer' : 'default', transition: 'transform 240ms cubic-bezier(.2,.8,.2,1), box-shadow 240ms ease, border-color 240ms ease' }),
          Platform.OS === 'web' ? ({ boxShadow: hovered ? '0 12px 28px -14px rgba(0,0,0,0.40)' : '0 1px 0 rgba(16,24,40,0.04)' } as object) : null,
          hovered ? web({ transform: 'translateY(-3px)' }) : null,
        ]}
      >
        {/* Reserve two label lines on mobile so the big numbers align across the
            grid regardless of whether a label wraps. */}
        <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 7, marginBottom: spacing[2], minHeight: isMobile ? 32 : undefined }}>
          <View style={{ width: 7, height: 7, borderRadius: 999, backgroundColor: dotColor, marginTop: 5 }} />
          <Text numberOfLines={2} style={{ flex: 1, fontSize: fontSize.sm, lineHeight: 16, color: colors.ink3, fontWeight: fontWeight.medium }}>{label}</Text>
          {detail && !isMobile && <Icon name="chev-d" size={13} color={hovered ? colors.ink3 : colors.ink4} style={{ marginTop: 1 }} />}
        </View>
        <Text style={{ fontSize: fontSize['3xl'], fontWeight: fontWeight.bold, color: valueColor, letterSpacing: -0.5 }}>{n}</Text>
        {!!sub && <Text numberOfLines={1} style={{ fontSize: fontSize.xs, color: colors.ink4, marginTop: 3, marginBottom: isMobile ? 14 : 0 }}>{sub}</Text>}
        {/* Mobile-only inline breakdown bar (replaces the desktop hover flyout).
            Pinned to the card bottom (marginTop:auto) so bars align across the
            grid even when a tile is stretched taller; an empty track shows when
            there's nothing to break down. */}
        {isMobile && (
          <View style={{ height: 5, borderRadius: 999, backgroundColor: colors.paper3, overflow: 'hidden', flexDirection: 'row', marginTop: 'auto' }}>
            {segs.map((s, i) => (
              <View key={i} style={{ width: `${(s.value / segTotal) * 100}%`, backgroundColor: s.color }} />
            ))}
          </View>
        )}
      </Pressable>

      {showFlyout && (
        <View style={web({ position: 'absolute', top: '100%', [detailAlign]: 0, paddingTop: 11, width: 320 }) as object}>
          <KpiDetailCard detail={detail!} caretAlign={detailAlign} />
        </View>
      )}
    </View>
  );
}

/** Generic premium hover-detail flyout for a KPI tile: gradient glyph header,
 *  count-up total, an optional grow+sheen split bar with legend, secondary
 *  stat rows, and a footer note. Mounts on hover so its motion replays. */
function KpiDetailCard({ detail, caretAlign }: { detail: KpiDetail; caretAlign: 'left' | 'right' }) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, font, radii } = theme;
  const totalUp = useCountUp(detail.total, 720);
  const mounted = useMounted();
  const segs = (detail.segments ?? []).filter((s) => s.value > 0);
  const segTotal = segs.reduce((a, s) => a + s.value, 0) || 1;
  const isEmpty = detail.total === 0 && segs.length === 0;

  return (
    <View
      style={[
        { backgroundColor: colors.card, borderWidth: 1, borderColor: colors.lineStrong, borderRadius: radii.lg, padding: 16, gap: 13, overflow: 'hidden' },
        web({
          boxShadow: `0 28px 64px -22px rgba(0,0,0,0.60), 0 4px 12px -6px rgba(0,0,0,0.28), 0 0 0 1px ${colors.line}`,
          animation: 'sk-rise 200ms cubic-bezier(.2,.9,.2,1) both',
        }),
      ]}
    >
      {/* pointer caret up toward the tile (near the aligned edge) */}
      <View style={web({ position: 'absolute', top: -6, [caretAlign]: 26, width: 12, height: 12, backgroundColor: colors.card, borderLeftWidth: 1, borderTopWidth: 1, borderColor: colors.lineStrong, transform: 'rotate(45deg)' }) as object} />
      {/* top gradient accent strip */}
      <View style={web({ position: 'absolute', top: 0, left: 16, right: 16, height: 2, borderRadius: 2, backgroundImage: `linear-gradient(90deg, transparent, ${colors.accent}, ${colors.teal}, transparent)` }) as object} />

      {/* header */}
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 9 }}>
        <View style={[{ width: 30, height: 30, borderRadius: 9, backgroundColor: colors.accent, alignItems: 'center', justifyContent: 'center' }, web({ backgroundImage: `linear-gradient(135deg, ${colors.accent}, ${colors.accentInk})` })]}>
          <Icon name={detail.icon} size={15} color={colors.onAccent} />
        </View>
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text numberOfLines={1} style={{ fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: colors.ink }}>{detail.title}</Text>
          <Text numberOfLines={1} style={{ fontSize: 10, color: colors.ink4, letterSpacing: 0.3 }}>{detail.subtitle}</Text>
        </View>
        {detail.badge != null && (
          <View style={{ paddingHorizontal: 8, height: 20, borderRadius: 999, borderWidth: 1, borderColor: colors.accentLine, backgroundColor: colors.accentSoft, alignItems: 'center', justifyContent: 'center' }}>
            <Text style={{ fontSize: 10, fontWeight: fontWeight.semibold, color: colors.accentInk }}>{detail.badge}</Text>
          </View>
        )}
      </View>

      {/* big count-up total */}
      <View style={{ flexDirection: 'row', alignItems: 'flex-end', gap: 7 }}>
        <Text style={{ fontFamily: font.mono, fontSize: 32, fontWeight: fontWeight.bold, color: colors.ink, letterSpacing: -0.8, fontVariant: ['tabular-nums'] }}>{totalUp.toLocaleString()}</Text>
        <Text style={{ fontSize: fontSize.sm, color: colors.ink4, marginBottom: 5 }}>{detail.unit}</Text>
      </View>

      {isEmpty ? (
        <Text style={{ fontSize: fontSize.sm, color: colors.ink3 }}>{detail.emptyText ?? 'Nothing to show right now.'}</Text>
      ) : (
        <>
          {segs.length > 0 && (
            <>
              {/* animated split bar — grows from 0 with a one-shot sheen */}
              <View style={{ height: 8, borderRadius: 999, backgroundColor: colors.paper3, overflow: 'hidden', flexDirection: 'row', position: 'relative' }}>
                {segs.map((s, i) => (
                  <View key={i} style={[{ width: `${mounted ? (s.value / segTotal) * 100 : 0}%`, backgroundColor: s.color }, web({ transition: `width 720ms cubic-bezier(.2,.8,.2,1) ${i * 60}ms` })]} />
                ))}
                <View style={web({ position: 'absolute', top: 0, bottom: 0, width: 40, backgroundImage: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent)', animation: 'sk-sheen 900ms ease-out 220ms 1' }) as object} />
              </View>

              {/* legend */}
              <View style={{ gap: 9 }}>
                {segs.map((s, i) => (
                  <LedgerLegend key={i} dotColor={s.color} label={s.label} value={s.value} pct={Math.round((s.value / segTotal) * 100)} font={font.mono} colors={colors} fontSize={fontSize} fontWeight={fontWeight} />
                ))}
              </View>
            </>
          )}

          {detail.stats && detail.stats.length > 0 && (
            <>
              <View style={{ height: 1, backgroundColor: colors.line }} />
              <View style={{ gap: 9 }}>
                {detail.stats.map((s, i) => (
                  <LedgerStat key={i} label={s.label} value={s.value} font={font.mono} colors={colors} fontSize={fontSize} fontWeight={fontWeight} />
                ))}
              </View>
            </>
          )}
        </>
      )}

      <View style={{ height: 1, backgroundColor: colors.line }} />
      <Text style={{ fontSize: 10, color: colors.ink4 }}>{detail.footer}</Text>
    </View>
  );
}

/* ── AI token-usage KPI tile (real spend · count-up + in/out split) ────── */

function AiUsageTile({ initial, delay = 0 }: { initial: { inputTokens: number; outputTokens: number; totalTokens: number; rerankRuns: number }; delay?: number }) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, font, radii, spacing } = theme;
  const { hovered, hoverProps } = useHover();
  const { isMobile } = useBreakpoints();
  const usage = useAiUsage(initial); // live over SSE
  const n = useCountUp(usage.totalTokens);
  const mounted = useMounted();
  const inPct = usage.totalTokens > 0 ? Math.round((usage.inputTokens / usage.totalTokens) * 100) : 0;
  const showFlyout = hovered && !isMobile && Platform.OS === 'web';

  // Wrapper carries the hover state so moving the pointer from the tile into the
  // detail card (a descendant) keeps it open; the transparent top strip on the
  // card bridges the visual gap so there's no dead zone to cross.
  return (
    <View
      {...hoverProps}
      style={[
        isMobile ? { flexBasis: '47%', flexGrow: 1, minWidth: 0, position: 'relative' } : { flex: 1, minWidth: 180, position: 'relative' },
        showFlyout ? web({ zIndex: 50 }) : null,
      ]}
    >
      <Pressable
        onPress={() => navigateApp('aiUsage')}
        style={[
          { padding: spacing[4], borderRadius: radii.lg, backgroundColor: colors.card, borderWidth: 1, borderColor: hovered ? colors.lineStrong : colors.line },
          isMobile ? { flex: 1 } : null,
          rise(delay),
          web({ cursor: 'pointer', transition: 'transform 240ms cubic-bezier(.2,.8,.2,1), box-shadow 240ms ease, border-color 240ms ease' }),
          Platform.OS === 'web' ? ({ boxShadow: hovered ? '0 12px 28px -14px rgba(0,0,0,0.40)' : '0 1px 0 rgba(16,24,40,0.04)' } as object) : null,
          hovered ? web({ transform: 'translateY(-3px)' }) : null,
        ]}
      >
        <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 7, marginBottom: spacing[2], minHeight: isMobile ? 32 : undefined }}>
          <View style={{ width: 7, height: 7, borderRadius: 999, backgroundColor: colors.accent, marginTop: 5 }} />
          <Text numberOfLines={2} style={{ flex: 1, fontSize: fontSize.sm, lineHeight: 16, color: colors.ink3, fontWeight: fontWeight.medium }}>AI tokens used</Text>
          {!isMobile && <Icon name="chev-d" size={13} color={hovered ? colors.ink3 : colors.ink4} style={{ marginTop: 1 }} />}
        </View>
        <Text style={{ fontSize: fontSize['3xl'], fontWeight: fontWeight.bold, color: colors.ink, letterSpacing: -0.5 }}>{n.toLocaleString()}</Text>
        <Text numberOfLines={1} style={{ fontSize: fontSize.xs, color: colors.ink4, marginTop: 3, marginBottom: isMobile ? 14 : 8, fontFamily: font.mono }}>
          {isMobile
            ? `${inPct}% in · ${100 - inPct}% out`
            : `${usage.inputTokens.toLocaleString()} in · ${usage.outputTokens.toLocaleString()} out · ${usage.rerankRuns} rerank${usage.rerankRuns === 1 ? '' : 's'}`}
        </Text>
        {/* input/output split (real proportions) — grows from 0 on mount (web).
            On mobile pinned to the card bottom so it aligns with the KPI bars. */}
        <View style={{ height: 5, borderRadius: 999, backgroundColor: colors.paper3, overflow: 'hidden', flexDirection: 'row', marginTop: isMobile ? 'auto' : 0 }}>
          <View
            style={[
              { width: `${mounted ? inPct : 0}%`, backgroundColor: colors.accent },
              web({ transition: 'width 900ms cubic-bezier(.2,.8,.2,1)' }),
            ]}
          />
          <View style={{ flex: 1, backgroundColor: colors.teal }} />
        </View>
      </Pressable>

      {/* Premium hover detail — full token ledger. Web-only (hover never fires on
          native/touch); mounts on hover so its entrance + count-up replay each time. */}
      {showFlyout && (
        <View style={web({ position: 'absolute', top: '100%', right: 0, paddingTop: 11, width: 340 }) as object}>
          <AiLedgerCard usage={usage} />
        </View>
      )}
    </View>
  );
}

/** The premium AI-token ledger flyout. A standalone component so its count-up +
 *  grow/sheen animations restart every time it mounts (i.e. on each hover). */
function AiLedgerCard({ usage }: { usage: { inputTokens: number; outputTokens: number; totalTokens: number; rerankRuns: number } }) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, font, radii } = theme;
  const total = usage.totalTokens;
  const inPct = total > 0 ? Math.round((usage.inputTokens / total) * 100) : 0;
  const outPct = total > 0 ? 100 - inPct : 0;
  const avgPerRerank = usage.rerankRuns > 0 ? Math.round(total / usage.rerankRuns) : 0;
  const totalUp = useCountUp(total, 720);
  const mounted = useMounted();

  return (
    <View
      style={[
        { backgroundColor: colors.card, borderWidth: 1, borderColor: colors.lineStrong, borderRadius: radii.lg, padding: 16, gap: 13, overflow: 'hidden' },
        web({
          boxShadow: `0 28px 64px -22px rgba(0,0,0,0.60), 0 4px 12px -6px rgba(0,0,0,0.28), 0 0 0 1px ${colors.line}`,
          animation: 'sk-rise 200ms cubic-bezier(.2,.9,.2,1) both',
        }),
      ]}
    >
      {/* pointer caret up toward the tile */}
      <View style={web({ position: 'absolute', top: -6, right: 26, width: 12, height: 12, backgroundColor: colors.card, borderLeftWidth: 1, borderTopWidth: 1, borderColor: colors.lineStrong, transform: 'rotate(45deg)' }) as object} />
      {/* top gradient accent strip */}
      <View style={web({ position: 'absolute', top: 0, left: 16, right: 16, height: 2, borderRadius: 2, backgroundImage: `linear-gradient(90deg, transparent, ${colors.accent}, ${colors.teal}, transparent)` }) as object} />

      {/* header */}
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 9 }}>
        <View style={[{ width: 30, height: 30, borderRadius: 9, backgroundColor: colors.accent, alignItems: 'center', justifyContent: 'center' }, web({ backgroundImage: `linear-gradient(135deg, ${colors.accent}, ${colors.accentInk})` })]}>
          <Icon name="zap" size={15} color={colors.onAccent} />
        </View>
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text style={{ fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: colors.ink }}>AI token ledger</Text>
          <Text style={{ fontSize: 10, color: colors.ink4, letterSpacing: 0.3 }}>Prepaid credits · SK Meter</Text>
        </View>
        <View style={{ paddingHorizontal: 8, height: 20, borderRadius: 999, borderWidth: 1, borderColor: colors.accentLine, backgroundColor: colors.accentSoft, alignItems: 'center', justifyContent: 'center' }}>
          <Text style={{ fontSize: 10, fontWeight: fontWeight.semibold, color: colors.accentInk }}>Metered</Text>
        </View>
      </View>

      {/* big count-up total */}
      <View style={{ flexDirection: 'row', alignItems: 'flex-end', gap: 7 }}>
        <Text style={{ fontFamily: font.mono, fontSize: 32, fontWeight: fontWeight.bold, color: colors.ink, letterSpacing: -0.8, fontVariant: ['tabular-nums'] }}>{totalUp.toLocaleString()}</Text>
        <Text style={{ fontSize: fontSize.sm, color: colors.ink4, marginBottom: 5 }}>tokens used</Text>
      </View>

      {/* animated in/out split — grows from 0 with a one-shot sheen */}
      <View style={{ height: 8, borderRadius: 999, backgroundColor: colors.paper3, overflow: 'hidden', flexDirection: 'row', position: 'relative' }}>
        <View style={[{ width: `${mounted ? inPct : 0}%`, backgroundColor: colors.accent }, web({ transition: 'width 720ms cubic-bezier(.2,.8,.2,1)' })]} />
        <View style={[{ width: `${mounted ? outPct : 0}%`, backgroundColor: colors.teal }, web({ transition: 'width 720ms cubic-bezier(.2,.8,.2,1) 60ms' })]} />
        <View style={web({ position: 'absolute', top: 0, bottom: 0, width: 40, backgroundImage: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent)', animation: 'sk-sheen 900ms ease-out 220ms 1' }) as object} />
      </View>

      {/* legend */}
      <View style={{ gap: 9 }}>
        <LedgerLegend dotColor={colors.accent} label="Input" value={usage.inputTokens} pct={inPct} font={font.mono} colors={colors} fontSize={fontSize} fontWeight={fontWeight} />
        <LedgerLegend dotColor={colors.teal} label="Output" value={usage.outputTokens} pct={outPct} font={font.mono} colors={colors} fontSize={fontSize} fontWeight={fontWeight} />
      </View>

      <View style={{ height: 1, backgroundColor: colors.line }} />

      {/* secondary stats */}
      <View style={{ gap: 9 }}>
        <LedgerStat label="Rerank runs" value={usage.rerankRuns.toLocaleString()} font={font.mono} colors={colors} fontSize={fontSize} fontWeight={fontWeight} />
        <LedgerStat label="Avg / rerank" value={`${avgPerRerank.toLocaleString()} tok`} font={font.mono} colors={colors} fontSize={fontSize} fontWeight={fontWeight} />
        <LedgerStat label="In : Out ratio" value={`${inPct}% : ${outPct}%`} font={font.mono} colors={colors} fontSize={fontSize} fontWeight={fontWeight} />
      </View>

      <View style={{ height: 1, backgroundColor: colors.line }} />

      {/* live footer */}
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 7 }}>
        <View style={{ width: 6, height: 6, borderRadius: 999, backgroundColor: colors.success }}>
          <View style={web({ position: 'absolute', top: -2, left: -2, width: 10, height: 10, borderRadius: 999, borderWidth: 1, borderColor: colors.success, animation: 'sk-pulse 1.8s ease-out infinite' }) as object} />
        </View>
        <Text style={{ fontSize: 10, color: colors.ink4, letterSpacing: 0.3 }}>Live · streaming from SK Meter</Text>
      </View>
    </View>
  );
}

/** One in/out legend line inside the AI token ledger card. */
function LedgerLegend({ dotColor, label, value, pct, font, colors, fontSize, fontWeight }: { dotColor: string; label: string; value: number; pct: number; font: string; colors: any; fontSize: any; fontWeight: any }) {
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
      <View style={{ width: 8, height: 8, borderRadius: 999, backgroundColor: dotColor }} />
      <Text style={{ flex: 1, fontSize: fontSize.sm, color: colors.ink2, fontWeight: fontWeight.medium }}>{label}</Text>
      <Text style={{ fontFamily: font, fontSize: fontSize.sm, color: colors.ink, fontWeight: fontWeight.semibold, fontVariant: ['tabular-nums'] }}>{value.toLocaleString()}</Text>
      <Text style={{ fontFamily: font, fontSize: fontSize.xs, color: colors.ink4, width: 34, textAlign: 'right' }}>{pct}%</Text>
    </View>
  );
}

/** One label/value row inside the AI token ledger card. */
function LedgerStat({ label, value, font, colors, fontSize, fontWeight }: { label: string; value: string; font: string; colors: any; fontSize: any; fontWeight: any }) {
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
      <Text style={{ fontSize: fontSize.sm, color: colors.ink3 }}>{label}</Text>
      <Text style={{ fontFamily: font, fontSize: fontSize.sm, color: colors.ink2, fontWeight: fontWeight.semibold }}>{value}</Text>
    </View>
  );
}

/* ── premium empty state ──────────────────────────────────────────────── */

function EmptyState({ icon, title, hint }: { icon: string; title: string; hint: string }) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight } = theme;
  return (
    <View style={{ alignItems: 'center', gap: 8, paddingVertical: 34, paddingHorizontal: 20 }}>
      <View style={{ width: 40, height: 40, borderRadius: 12, backgroundColor: colors.paper2, borderWidth: 1, borderColor: colors.line, alignItems: 'center', justifyContent: 'center' }}>
        <Icon name={icon} size={19} color={colors.ink3} />
      </View>
      <Text style={{ fontSize: fontSize.md, color: colors.ink2, fontWeight: fontWeight.semibold }}>{title}</Text>
      <Text style={{ fontSize: fontSize.xs, color: colors.ink4, textAlign: 'center' }}>{hint}</Text>
    </View>
  );
}

/* ── recent-run row ───────────────────────────────────────────────────── */

function RunRow({ run, index, last }: { run: DashboardMatchRun; index: number; last: boolean }) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, font, radii } = theme;
  const { hovered, hoverProps } = useHover();
  const vm = rerankVM(run.rerankStatus);
  const clientLine = `${run.client ?? 'In-house'} · ${relTime(run.ranAt)} · ${TRIGGER_LABEL[run.trigger]}`;
  const Funnel = ({ n, label }: { n: number; label: string }) => (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5 }}>
      <Text style={{ fontFamily: font.mono, fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: colors.ink2 }}>{n}</Text>
      <Text style={{ fontSize: 10, color: colors.ink4 }}>{label}</Text>
    </View>
  );
  return (
    <Pressable
      {...hoverProps}
      onPress={() => navigateApp('jobs')}
      style={[
        { flexDirection: 'row', alignItems: 'center', gap: 12, paddingHorizontal: 16, paddingVertical: 13, borderBottomColor: colors.line, borderBottomWidth: last ? 0 : 1, backgroundColor: hovered ? colors.paper2 : 'transparent' },
        web({ cursor: 'pointer', transition: 'background 160ms ease' }),
        rise(190 + index * 45),
      ]}
    >
      {/* leading role glyph */}
      <View style={[{ width: 34, height: 34, borderRadius: 9, backgroundColor: colors.paper2, borderWidth: 1, borderColor: hovered ? colors.lineStrong : colors.line, alignItems: 'center', justifyContent: 'center' }, web({ transition: 'border-color 160ms ease' })]}>
        <Icon name="layers" size={15} color={colors.ink3} />
      </View>
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text numberOfLines={1} style={{ fontSize: fontSize.md, color: colors.ink, fontWeight: fontWeight.semibold }}>{run.role}</Text>
        <Text numberOfLines={1} style={{ fontSize: fontSize.xs, color: colors.ink3, marginTop: 1 }}>{clientLine}</Text>
        {/* funnel chips */}
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 7, marginTop: 5 }}>
          <Funnel n={run.stage1Count} label="gated" />
          <Icon name="chev-r" size={11} color={colors.ink4} />
          <Funnel n={run.stage2Count} label="cosine" />
          <Icon name="chev-r" size={11} color={colors.ink4} />
          <Funnel n={run.rankedCount} label="ranked" />
        </View>
      </View>
      <StatusChip tone={vm.tone} label={vm.label} />
      <Icon name="chev-r" size={15} color={hovered ? colors.ink3 : colors.transparent} />
    </Pressable>
  );
}

/* ── ingest stat row (mini proportion bar + count-up) ─────────────────── */

function IngestRow({ label, value, max, tone, last, delay = 0 }: { label: string; value: number; max: number; tone: Tone; last?: boolean; delay?: number }) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, font } = theme;
  const dot: Record<string, string> = { neutral: colors.neutral, info: colors.info, success: colors.success, danger: colors.danger };
  const c = dot[tone] ?? colors.ink3;
  const n = useCountUp(value);
  const mounted = useMounted();
  const pct = Math.round((value / max) * 100);
  return (
    <View style={[{ paddingVertical: 11, paddingHorizontal: 10, borderBottomColor: colors.line, borderBottomWidth: last ? 0 : 1 }, rise(delay)]}>
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 7 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <View style={{ width: 7, height: 7, borderRadius: 999, backgroundColor: c }} />
          <Text style={{ fontSize: fontSize.md, color: colors.ink2 }}>{label}</Text>
        </View>
        <Text style={{ fontFamily: font.mono, fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: colors.ink }}>{n}</Text>
      </View>
      <View style={{ height: 4, borderRadius: 999, backgroundColor: colors.paper3, overflow: 'hidden' }}>
        <View style={[{ width: `${mounted ? pct : 0}%`, height: 4, borderRadius: 999, backgroundColor: c }, web({ transition: 'width 760ms cubic-bezier(.2,.8,.2,1)', transitionDelay: `${delay}ms` })]} />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  row: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingHorizontal: 16, paddingVertical: 12 },
  empty: { padding: 20, textAlign: 'center', fontSize: 12, color: '#9E9E9E' },
});
